import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  ArrowLeft,
  QrCode,
  Users,
  TrendingUp,
  Building2,
  Loader2,
  Eye,
  Package,
  Download,
  UserCheck,
  Radio,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import type { EventStatsDto, EventStatsPeriod } from "@mvk/shared";
import { isApiEnabled } from "../../api/config";
import { useAdminEvent } from "../hooks/useAdminEvent";
import { adminStatsApi } from "../../services/adminStatsApi";

function StatCard({
  label,
  value,
  sub,
  icon,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
          {icon}
        </div>
      </div>
      <div className="text-[22px] text-gray-900 tabular-nums">{value}</div>
      <div className="text-[12px] text-gray-500 mt-0.5">{label}</div>
      {sub ? <div className="text-[11px] text-gray-400 mt-0.5 leading-snug">{sub}</div> : null}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-[15px] font-medium text-gray-900">{title}</h2>
      {children}
    </section>
  );
}

function RankTable({
  title,
  headers,
  rows,
  empty = "Пока нет данных",
}: {
  title: string;
  headers: string[];
  rows: Array<{ key: string; cells: Array<string | number> }>;
  empty?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <h3 className="text-[13px] text-gray-900 mb-3">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-[12px] text-gray-400">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-50">
                <th className="pb-2 pr-2 font-normal w-6">#</th>
                {headers.map((h) => (
                  <th key={h} className="pb-2 pr-2 font-normal">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.key} className="border-b border-gray-50 last:border-0">
                  <td className="py-2 pr-2 text-gray-400 align-top">{i + 1}</td>
                  {row.cells.map((cell, j) => (
                    <td
                      key={`${row.key}-${j}`}
                      className={`py-2 pr-2 align-top ${
                        j === 0 ? "text-gray-800 max-w-[180px] truncate" : "text-gray-600 tabular-nums whitespace-nowrap"
                      }`}
                      title={j === 0 ? String(cell) : undefined}
                    >
                      {typeof cell === "number" ? cell.toLocaleString("ru-RU") : cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function exportStatsCsv(stats: EventStatsDto, title: string) {
  const v = stats.visitors;
  const e = stats.exhibitors;
  const rows: string[][] = [
    ["Метрика", "Значение"],
    ["Мероприятие", title],
    ["Период", stats.period],
  ];
  if (v) {
    rows.push(
      ["Посетителей с доступом", String(v.withAccess)],
      ["Посетителей авторизовались", String(v.authorized)],
      ["Посетителей сейчас в приложении", String(v.onlineNow)],
      ["Сканов беджей представителей (посетители)", String(v.repBadgeScans.scans)],
      ["Уник. посетителей (бейджи представителей)", String(v.repBadgeScans.uniqueActors)],
      ["Сканов QR стендов (посетители)", String(v.boothScans.scans)],
      ["Уник. посетителей (QR стендов)", String(v.boothScans.uniqueActors)],
      ["Сканов QR товаров (посетители)", String(v.productScans.scans)],
      ["Уник. посетителей (QR товаров)", String(v.productScans.uniqueActors)],
    );
  }
  if (e) {
    rows.push(
      ["Компаний", String(e.companies)],
      ["Представителей с доступом", String(e.repsWithAccess)],
      ["Представителей авторизовались", String(e.repsAuthorized)],
      ["Компаний с ≥1 авторизованным стендистом", String(e.companiesWithAuthorizedRep)],
      ["Компаний с QR товаров", String(e.companiesWithProductQr)],
      ["QR-кодов товаров создано", String(e.productQrCodesCreated)],
      ["Представителей сейчас в приложении", String(e.repsOnlineNow)],
      ["Сканов беджей посетителей (представители)", String(e.visitorBadgeScans.scans)],
      ["Уник. представителей (бейджи посетителей)", String(e.visitorBadgeScans.uniqueActors)],
    );
  }
  const csv = rows
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
    .join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `mvk-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function AdminStatsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { event } = useAdminEvent(id);
  const [period, setPeriod] = useState<EventStatsPeriod>("event");
  const [nativeTicketsOnly, setNativeTicketsOnly] = useState(false);
  const [stats, setStats] = useState<EventStatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const title = event?.title ?? "";

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");

    if (!isApiEnabled()) {
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      const statsRes = await adminStatsApi.getEventStats(id, period, {
        nativeTicketsOnly,
      });
      setStats(statsRes.stats);
      // If event is not in a group, force toggle off (only when it was on).
      if (
        nativeTicketsOnly &&
        statsRes.stats.groupFilter &&
        !statsRes.stats.groupFilter.available
      ) {
        setNativeTicketsOnly(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось загрузить статистику");
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [id, period, nativeTicketsOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center text-gray-400">
        <Loader2 size={20} className="animate-spin mr-2" /> Загрузка…
      </div>
    );
  }

  if (!stats) {
    return <div className="p-6 text-gray-500">Мероприятие не найдено</div>;
  }

  const v = stats.visitors;
  const exh = stats.exhibitors;
  const r = stats.rankings;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-8">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(`/events/${id}`)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-[19px] text-gray-900">Аналитика</h1>
          <p className="text-[13px] text-gray-400 mt-0.5 truncate">{title}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            type="button"
            onClick={() => exportStatsCsv(stats, title)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-[12px] text-gray-600 hover:bg-gray-50"
            title="Выгрузить CSV"
          >
            <Download size={14} />
            <span className="hidden sm:inline">CSV</span>
          </button>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {(
              [
                { id: "day" as const, label: "День" },
                { id: "event" as const, label: "Мероприятие" },
                { id: "all" as const, label: "Всё время" },
              ] as const
            ).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 rounded-md text-[12px] transition-colors ${
                  period === p.id
                    ? "bg-white shadow-sm text-indigo-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {stats.groupFilter?.available ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-xl border border-indigo-100 bg-indigo-50/50 px-4 py-3">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              checked={nativeTicketsOnly}
              onChange={(e) => setNativeTicketsOnly(e.target.checked)}
            />
            <span className="text-[13px] text-gray-800">
              Только посетители этой выставки
            </span>
          </label>
          <p className="text-[11px] text-gray-500 leading-snug sm:flex-1">
            {nativeTicketsOnly
              ? "Считаем тех, у кого есть билет/баркод именно на это мероприятие (без доступа из соседних выставок группы)."
              : stats.groupFilter.groupTitle
                ? `Сейчас — все с доступом, включая соседние выставки группы «${stats.groupFilter.groupTitle}».`
                : "Сейчас — все с доступом, включая соседние выставки группы."}
          </p>
        </div>
      ) : null}

      {error ? <p className="text-[13px] text-amber-600">{error}</p> : null}

      {v ? (
        <Section title="Посетители">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <StatCard
              label="Посетителей с доступом"
              value={v.withAccess.toLocaleString("ru-RU")}
              sub={
                nativeTicketsOnly
                  ? "билеты/баркоды этой выставки"
                  : "билеты / доступ к мероприятию"
              }
              icon={<Users size={16} className="text-indigo-500" />}
            />
            <StatCard
              label="Посетителей авторизовались"
              value={v.authorized.toLocaleString("ru-RU")}
              sub={`из ${v.withAccess.toLocaleString("ru-RU")} с доступом`}
              icon={<UserCheck size={16} className="text-emerald-500" />}
            />
            <StatCard
              label="Сейчас в приложении"
              value={v.onlineNow.toLocaleString("ru-RU")}
              sub={`активность за ${v.onlineWindowMinutes} мин`}
              icon={<Radio size={16} className="text-sky-500" />}
            />
            <StatCard
              label="Сканы беджей представителей"
              value={v.repBadgeScans.scans.toLocaleString("ru-RU")}
              sub={`${v.repBadgeScans.uniqueActors.toLocaleString("ru-RU")} уник. посетителей`}
              icon={<QrCode size={16} className="text-violet-500" />}
            />
            <StatCard
              label="Сканы QR стендов"
              value={v.boothScans.scans.toLocaleString("ru-RU")}
              sub={`${v.boothScans.uniqueActors.toLocaleString("ru-RU")} уник. посетителей`}
              icon={<Building2 size={16} className="text-indigo-500" />}
            />
            <StatCard
              label="Сканы QR товаров"
              value={v.productScans.scans.toLocaleString("ru-RU")}
              sub={`${v.productScans.uniqueActors.toLocaleString("ru-RU")} уник. посетителей`}
              icon={<Package size={16} className="text-amber-500" />}
            />
          </div>
        </Section>
      ) : null}

      {exh ? (
        <Section title="Экспоненты">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              label="Компаний"
              value={exh.companies.toLocaleString("ru-RU")}
              icon={<Building2 size={16} className="text-indigo-500" />}
            />
            <StatCard
              label="Представителей с доступом"
              value={exh.repsWithAccess.toLocaleString("ru-RU")}
              sub="стендисты / представители"
              icon={<Users size={16} className="text-violet-500" />}
            />
            <StatCard
              label="Представителей авторизовались"
              value={exh.repsAuthorized.toLocaleString("ru-RU")}
              sub={`из ${exh.repsWithAccess.toLocaleString("ru-RU")} с доступом`}
              icon={<UserCheck size={16} className="text-emerald-500" />}
            />
            <StatCard
              label="Компаний с авторизованным стендистом"
              value={exh.companiesWithAuthorizedRep.toLocaleString("ru-RU")}
              sub="≥1 представитель в приложении"
              icon={<Building2 size={16} className="text-sky-500" />}
            />
            <StatCard
              label="Компаний создали QR товаров"
              value={exh.companiesWithProductQr.toLocaleString("ru-RU")}
              icon={<QrCode size={16} className="text-amber-500" />}
            />
            <StatCard
              label="QR-кодов товаров создано"
              value={exh.productQrCodesCreated.toLocaleString("ru-RU")}
              icon={<Package size={16} className="text-violet-500" />}
            />
            <StatCard
              label="Представителей сейчас в приложении"
              value={exh.repsOnlineNow.toLocaleString("ru-RU")}
              sub={`активность за ${exh.onlineWindowMinutes} мин`}
              icon={<Radio size={16} className="text-rose-500" />}
            />
            <StatCard
              label="Сканы беджей посетителей"
              value={exh.visitorBadgeScans.scans.toLocaleString("ru-RU")}
              sub={`${exh.visitorBadgeScans.uniqueActors.toLocaleString("ru-RU")} уник. представителей`}
              icon={<QrCode size={16} className="text-indigo-500" />}
            />
          </div>
        </Section>
      ) : null}

      {r ? (
        <Section title="Рейтинги компаний">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RankTable
              title="Топ по просмотру карточек"
              headers={["Компания", "Просмотры", "В избранном"]}
              rows={r.companiesByViews.map((c) => ({
                key: c.companyId,
                cells: [c.name, c.views, c.favorites],
              }))}
            />
            <RankTable
              title="Топ по сканированию QR стендов"
              headers={["Компания", "Сканирований"]}
              rows={r.companiesByBoothScans.map((c) => ({
                key: c.companyId,
                cells: [c.booth ? `${c.name} (${c.booth})` : c.name, c.scans],
              }))}
            />
            <RankTable
              title="Топ товаров по интересу"
              headers={["Товар", "Просмотры", "QR-сканы"]}
              rows={r.productsByInterest.map((p) => ({
                key: p.productId,
                cells: [`${p.name} · ${p.companyName}`, p.views, p.qrScans],
              }))}
            />
            <RankTable
              title="Топ по сбору лидов"
              headers={["Компания", "Лидов"]}
              rows={r.companiesByLeads.map((c) => ({
                key: c.companyId,
                cells: [c.name, c.leads],
              }))}
            />
          </div>
        </Section>
      ) : null}

      {stats.activityByDay && stats.activityByDay.length > 0 ? (
        <Section title="Активность по дням">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-[11px] text-gray-400 mb-3">
              Пики по дням выставки · уникальные сканеры и всего сканов
            </p>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={stats.activityByDay} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="visitors" name="Посетители" fill="#6366f1" radius={[3, 3, 0, 0]} />
                <Bar dataKey="exhibitors" name="Представители" fill="#a78bfa" radius={[3, 3, 0, 0]} />
                <Bar dataKey="scans" name="Сканов всего" fill="#c7d2fe" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      ) : null}

      {stats.activityByHour && stats.activityByHour.length > 0 ? (
        <Section title="Активность по часам">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-[11px] text-gray-400 mb-3">
              Уникальные сканеры: посетители и представители
            </p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.activityByHour} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="hour"
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(h) => `${h}:00`}
                />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 12 }}
                  labelFormatter={(l) => `${l}:00`}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="visitors" name="Посетители" fill="#6366f1" radius={[3, 3, 0, 0]} />
                <Bar dataKey="exhibitors" name="Представители" fill="#a78bfa" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      ) : null}

      {r ? (
        <Section title="Интерес к программе">
          <RankTable
            title="Сессии в избранном"
            headers={["Сессия", "Всего", "Посетители", "Представители"]}
            rows={r.sessionsByFavorites.map((s) => ({
              key: s.sessionId,
              cells: [s.title, s.favorites, s.byVisitors, s.byExhibitors],
            }))}
            empty="Пока никто не добавил сессии в избранное"
          />
        </Section>
      ) : null}

      {!v &&
      !exh &&
      !r?.companiesByViews.length &&
      !stats.activityByHour?.length &&
      !stats.activityByDay?.length ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-gray-400 text-[13px]">
          <TrendingUp size={24} className="mx-auto mb-2 opacity-50" />
          Нет данных за выбранный период
        </div>
      ) : null}
    </div>
  );
}
